/**
 * An Inset layout component that adds allows you to control the negative space
 * around its children.
 */
import * as React from "react"
import styled, { ThemedStyledProps } from "@oui/system"
import { CompositeWidgetRole, ContentRole, WidgetRole } from "@oui/system/roles"
import { omit } from "ramda"
import { px } from "@oui/system/units"
import { Space } from "@oui/system/spacing"
import { propsToMediaQueryStyles, Responsive } from "@oui/system/breakpoints"
import Box from "../Box"

export type Spacing =
  | [Space]
  | [Space, Space]
  | [Space, Space, Space]
  | [Space, Space, Space, Space]

type DefaultProps = typeof defaultProps
type Props = Readonly<
  {
    /**
     * ARIA defines "role" semantics which can be applied to elements, in the case
     * of an Inset composite, content, or widget roles are allowed.
     */
    role?: CompositeWidgetRole | ContentRole | WidgetRole
  } & React.ComponentPropsWithoutRef<typeof Box> &
    DefaultProps
>

const defaultProps = {
  /**
   * The amount of space content is inset.
   * @default ["sm"]
   */
  spacing: ["sm"] as Responsive<Spacing>
}

const toParts = ([top, right = top, bottom = top, left = right]: Spacing): [
  Space,
  Space,
  Space,
  Space
] => [top, right, bottom, left]

const padding = ({
  theme,
  spacing = ["sm"]
}: ThemedStyledProps<{ spacing: Spacing }>): { padding: string } => ({
  padding: toParts(spacing as Spacing)
    .map((space: Space): string => px(theme.spacing[space]))
    .join(" ")
})

const interpolations = {
  spacing: padding
}

const Inset_ = (props: Props) => (
  <Box {...omit(Object.keys(interpolations), props)} />
)
Inset_.defaultProps = defaultProps

const Inset = styled(Inset_)(
  propsToMediaQueryStyles(Object.values(interpolations))
)
Inset.defaultProps = defaultProps

export default Inset
